import SvgIcon from '@mui/material/SvgIcon';
import { css, type Theme } from '@mui/material/styles';
import type { SerializedStyles } from '@mui/styled-engine';
import { CheckCircleIcon } from '@phosphor-icons/react/CheckCircle';
import { InfoIcon } from '@phosphor-icons/react/Info';
import { WarningCircleIcon } from '@phosphor-icons/react/WarningCircle';
import {
  closeSnackbar,
  type SnackbarKey,
  SnackbarProvider as SnackbarProviderNotistack,
  type SnackbarProviderProps as SnackbarProviderNotistackProps,
} from 'notistack';
import { forwardRef, type JSX, useCallback } from 'react';
import { ELEVATION } from '../theme/elevation.ts';
import { CloseNotificationButton } from './CloseNotificationButton.tsx';

const WarningOutlined = () => (
  <SvgIcon data-test="snackbar-warning-icon">
    <path d="M12 5.99 19.53 19H4.47L12 5.99M12 2 1 21h22L12 2zm1 14h-2v2h2v-2zm0-6h-2v4h2v-4z"></path>
  </SvgIcon>
);

export const SnackbarProvider = forwardRef<SnackbarProviderNotistack, SnackbarProviderNotistackProps>(
  ({ children, anchorOrigin = { horizontal: 'center', vertical: 'bottom' }, ...restProps }, ref): JSX.Element => {
    const action = useCallback((key: SnackbarKey) => <CloseNotificationButton action={() => closeSnackbar(key)} />, []);
    return (
      <SnackbarProviderNotistack
        ref={ref}
        autoHideDuration={15000}
        anchorOrigin={anchorOrigin}
        preventDuplicate
        iconVariant={{
          error: <WarningCircleIcon />,
          info: <InfoIcon />,
          success: <CheckCircleIcon />,
          warning: <WarningOutlined />,
        }}
        action={action}
        {...restProps}
      >
        {children}
      </SnackbarProviderNotistack>
    );
  }
);

export const snakbarStyles = (theme: Theme): SerializedStyles => css`
  .SnackbarContainer-root {
    bottom: 32px !important;
    gap: 12px;

    & > .MuiCollapse-root > .MuiCollapse-wrapper {
      padding: 0 !important;
    }
  }

  .notistack-SnackbarContainer {
    & #notistack-snackbar {
      font-size: 13px;
      line-height: 28px;
      max-width: 570px;
      min-width: 0;
      overflow-wrap: anywhere;

      & svg {
        flex-shrink: 0;
        font-size: 18px;
        height: 28px;
        margin-inline-end: 11px;
      }

      & ~ div {
        padding-left: 32px;
        margin-right: 0;
      }
    }

    & .notistack-MuiContent {
      padding: 12px 16px;
      color: ${theme.palette.text.primary};
      box-shadow: ${ELEVATION.elevation8} !important;
    }

    & .notistack-MuiContent-error {
      background-color: ${theme.palette.error.light};

      & svg, & button {
        color: ${theme.palette.error.main};
      }

      & .MuiIconButton-root:hover {
        background-color: ${theme.palette.action.hover};
      }
    }

    & .notistack-MuiContent-info {
      background-color: ${theme.palette.primary.light};

      & svg, & button {
        color: ${theme.palette.primary.main};
      }

      & .MuiIconButton-root:hover {
        background-color: ${theme.palette.action.hover};
      }
    }

    & .notistack-MuiContent-success {
      background-color: ${theme.palette.success.light};

      & svg, & button {
        color: ${theme.palette.success.main};
      }

      & .MuiIconButton-root:hover {
        background-color: ${theme.palette.action.hover};
      }
    }

    & .notistack-MuiContent-warning {
      background-color: ${theme.palette.warning.light};

      & svg, & button {
        color: ${theme.palette.warning.main};
      }

      & .MuiIconButton-root:hover {
        background-color: ${theme.palette.action.hover};
      }
    }
  }
`;
